import React from 'react';
import styles from './LocationFilters.module.css';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import { FloDropdown, FloFilterGroup, FloGroupedDropdown, FloFilterCheckbox } from 'components';
import { LocationType, LocationFilters as Filters, LocationFacet, LocationFacetType } from 'model';
import { LocationFilterState, LocationFacetsData } from 'model/state';


type LocationFiltersProps = {
  filterData: LocationFacetsData,
  filters: LocationFilterState,
  updateLocationFilters: (filters: Filters) => void,
  fetchLocationFacets: (facet: LocationFacet) => void,
  userId: string | undefined,
  isRootLevel: boolean,
}

export const LocationFilters: React.FC<LocationFiltersProps> = React.memo(({
  filterData,
  filters,
  updateLocationFilters,
  fetchLocationFacets,
  userId,
  isRootLevel,
}) => {
  const { t } = useTranslation();

  const clearDropdown = (filters: Filters, type?: LocationFacetType) => {
    updateLocationFilters(filters);
    if (type !== undefined) {
      fetchLocationFacets({ userId: userId as string, search: '', page: 1, type })
    }
  };

  return (
    <Row className={styles.root}>
      <Col xs="12" md="6" lg="4" xl>
        <FloFilterGroup label={t('Type')} onClear={() => clearDropdown({ type: null })}>
          <FloDropdown
            id        = "location-type-filter"
            title     = {t('Select Type')}
            options   = {filterData.types.rows || []}
            values    = {filters.type === null ? null : LocationType[filters.type]}
            onChange  = {e => updateLocationFilters({ type: LocationType[e.value as keyof typeof LocationType] })}
          />
        </FloFilterGroup>
      </Col>
      <Col xs="12" md="6" lg="4" xl>
        <FloGroupedDropdown
          groupLabel  = {t('City')}
          onClear     = {() => clearDropdown({ cities: [] }, LocationFacetType.CITY)}
          reinitList  = {() => fetchLocationFacets({ userId: userId as string, search: '', page: 1, type: LocationFacetType.CITY })}
          id          = "city-filter"
          title       = {t('Select City')}
          options     = {filterData.cities.rows || []}
          values      = {filters.cities}
          onChange    = {e => updateLocationFilters({ cities: e ? e.map((d: any) => d.value) : [] })}
          multiple
          disableMemoryFilter
          isLoading             = {filterData.cities.isLoading || false}
          loadingMessage        = {() => t('Loading...')}
          page                  = {filterData.cities.page || 1}
          hasMore               = {(filterData.cities.total || 0) > (filterData.cities.rows?.length || 0)}
          onLoadOptions         = {(search: string | undefined, page: number) => {
            fetchLocationFacets({
              userId: userId as string,
              search: search || '',
              page,
              type: LocationFacetType.CITY
            })
          }}
        />
      </Col>
      <Col xs="12" md="6" lg="4" xl>
        <FloGroupedDropdown
          groupLabel  = {t('State')}
          onClear     = {() => clearDropdown({ states: [] }, LocationFacetType.STATE)}
          reinitList  = {() => fetchLocationFacets({ userId: userId as string, search: '', page: 1, type: LocationFacetType.STATE })}
          id          = "state-filter"
          title       = {t('Select State')}
          options     = {filterData.states.rows || []}
          values      = {filters.states}
          onChange    = {e => updateLocationFilters({ states: e ? e.map((d: any) => d.value) : [] })}
          multiple
          disableMemoryFilter
          isLoading             = {filterData.states.isLoading || false}
          loadingMessage        = {() => t('Loading...')}
          page                  = {filterData.states.page || 1}
          hasMore               = {(filterData.states.total || 0) > (filterData.states.rows?.length || 0)}
          onLoadOptions         = {(search: string | undefined, page: number) => {
            fetchLocationFacets({
              userId: userId as string,
              search: search || '',
              page,
              type: LocationFacetType.STATE
            })
          }}
        />
      </Col>
      <Col xs="12" md="6" lg="4" xl>
        <FloGroupedDropdown
          groupLabel  = {t('Country')}
          onClear     = {() => clearDropdown({ countries: [] }, LocationFacetType.COUNTRY)}
          reinitList  = {() => fetchLocationFacets({ userId: userId as string, search: '', page: 1, type: LocationFacetType.COUNTRY })}
          id          = "country-filter"
          title       = {t('Select Country')}
          options     = {filterData.countries.rows || []}
          values      = {filters.countries}
          onChange    = {e => updateLocationFilters({ countries: e ? e.map((d: any) => d.value) : [] })}
          multiple
          isLoading             = {filterData.countries.isLoading || false}
          loadingMessage        = {() => t('Loading...')}
          page                  = {filterData.countries.page || 1}
          hasMore               = {(filterData.countries.total || 0) > (filterData.countries.rows?.length || 0)}
        />
      </Col>
      <Col xs="12" md="6" lg="4" xl>
        <FloGroupedDropdown
          groupLabel  = {t('Zip')}
          onClear     = {() => clearDropdown({ zipCodes: [] }, LocationFacetType.POSTAL_CODE)}
          reinitList  = {() => fetchLocationFacets({ userId: userId as string, search: '', page: 1, type: LocationFacetType.POSTAL_CODE })}
          id          = "zip-filter"
          title       = {t('Select Zip Code')}
          options     = {filterData.postalCodes.rows || []}
          values      = {filters.zipCodes}
          onChange    = {e => updateLocationFilters({ zipCodes: e ? e.map((d: any) => d.value) : [] })}
          multiple
          disableMemoryFilter
          isLoading             = {filterData.postalCodes.isLoading || false}
          loadingMessage        = {() => t('Loading...')}
          page                  = {filterData.postalCodes.page || 1}
          hasMore               = {(filterData.postalCodes.total || 0) > (filterData.postalCodes.rows?.length || 0)}
          onLoadOptions         = {(search: string | undefined, page: number) => {
            fetchLocationFacets({
              userId: userId as string,
              search: search || '',
              page,
              type: LocationFacetType.POSTAL_CODE
            });
          }}
        />
      </Col>
      <Col xs="12" md="6" lg="4" xl>
        <FloFilterCheckbox
          checked     = {filters.offlineDevices}
          groupLabel  = {t('Offline devices')}
          onChange    = {() => updateLocationFilters({ offlineDevices: !filters.offlineDevices })}
        />
      </Col>
    </Row>
  );
})